/**
 * El fondo alterno de una sección —normal o secundario—, puesto con su ESTILO
 * de sección («Sección base» / «Sección secundaria») y no con una referencia de
 * color suelta.
 *
 * La IA, la vista previa del asistente y el soltar un template lo ponían con
 * `styleRefs.colors.background`. Se veía igual, pero la sección quedaba a
 * medias: el estilo seguía enlazado y aun así «Apariencia» dejaba cambiar el
 * fondo, porque una referencia de color propia gana al estilo. Y no era lo
 * mismo que deja «Automático» (apagar y volver a prender lo arreglaba). Así
 * sale ya como lo deja ese interruptor.
 *
 * Sólo se toca una sección que sigue a esos dos estilos con un fondo de la
 * marca. Una con foto, de marca (`section-accent`) o con un color propio es una
 * decisión de diseño y se queda como está.
 */
export const SECTION_TONE_STYLE = { default: 'section-default', secondary: 'section-secondary' };
const TONE_COLOR_REFS = new Set(['background', 'backgroundSecondary']);
const TONE_CSS = new Set(['var(--wb-background)', 'var(--wb-backgroundSecondary)']);
const BACKGROUND_KEYS = ['background', 'backgroundColor'];
const BREAKPOINTS = ['desktop', 'tablet', 'mobile'];
const blank = (value) => typeof value !== 'string' || value.trim() === '' || value === 'transparent' || value === 'none';
const toneCss = (value) => typeof value === 'string' && TONE_CSS.has(value.replace(/\s+/g, ''));
/** Si la sección lleva el fondo normal o el secundario y se puede alternar. */
export function takesSectionTone(node) {
    if (node.type !== 'section')
        return false;
    const refs = node.styleRefs;
    const section = refs?.section;
    if (section && section !== SECTION_TONE_STYLE.default && section !== SECTION_TONE_STYLE.secondary)
        return false;
    if (refs?.overrides?.some((key) => key === 'background' || key === 'backgroundColor' || key === 'backgroundImage'))
        return false;
    for (const key of BACKGROUND_KEYS) {
        const ref = refs?.colors?.[key];
        if (ref && !TONE_COLOR_REFS.has(ref))
            return false;
    }
    return BREAKPOINTS.every((bp) => {
        const styles = node.styles?.[bp];
        if (!styles)
            return true;
        if (!blank(styles.backgroundImage))
            return false;
        return BACKGROUND_KEYS.every((key) => blank(styles[key]) || toneCss(styles[key]));
    });
}
/** El tono que lleva por su referencia de color suelta, si lleva una. */
export function sectionToneColorRef(node) {
    const ref = node.styleRefs?.colors?.background ?? node.styleRefs?.colors?.backgroundColor;
    if (ref === 'background')
        return 'default';
    if (ref === 'backgroundSecondary')
        return 'secondary';
    return null;
}
/**
 * La sección con el fondo normal o el secundario, por su estilo de sección.
 * Quita la referencia de color y el color escrito que hacían lo mismo, y la
 * marca de «fijada a mano» que un template puede traer de cuando se diseñó
 * (con ella la sección no se movía al alternar). Una
 * sección sin estilo de sección se queda sin él y cambia sólo de rol: ponerle
 * uno le traería también su relleno.
 */
export function withSectionTone(node, secondary) {
    if (!takesSectionTone(node))
        return node;
    const refs = { ...(node.styleRefs ?? {}) };
    if (refs.colors) {
        const colors = { ...refs.colors };
        for (const key of BACKGROUND_KEYS) {
            const ref = colors[key];
            if (ref && TONE_COLOR_REFS.has(ref))
                delete colors[key];
        }
        if (Object.keys(colors).length)
            refs.colors = colors;
        else
            delete refs.colors;
    }
    if (refs.section)
        refs.section = secondary ? SECTION_TONE_STYLE.secondary : SECTION_TONE_STYLE.default;
    let styles = node.styles;
    if (styles) {
        const next = { ...styles };
        for (const bp of BREAKPOINTS) {
            const bag = next[bp];
            if (!bag || !BACKGROUND_KEYS.some((key) => toneCss(bag[key])))
                continue;
            const clean = { ...bag };
            for (const key of BACKGROUND_KEYS)
                if (toneCss(clean[key]))
                    delete clean[key];
            next[bp] = clean;
        }
        styles = next;
    }
    const { sectionToneManual: _manual, ...rest } = node;
    return {
        ...rest,
        backgroundRole: secondary ? 'surface' : 'background',
        styleRefs: refs,
        ...(styles ? { styles } : {}),
    };
}
/**
 * Las secciones de una página que traen el tono con la referencia de color
 * suelta, pasadas a su estilo de sección. Para los proyectos creados antes de
 * esto; devuelve la misma lista si no había nada que cambiar.
 */
export function sectionTonesByStyle(elements) {
    let changed = false;
    const next = elements.map((node) => {
        const tone = sectionToneColorRef(node);
        if (!tone || !takesSectionTone(node))
            return node;
        changed = true;
        return withSectionTone(node, tone === 'secondary');
    });
    return changed ? next : elements;
}
//# sourceMappingURL=sectionTone.js.map